<script setup>
// Assistente de IA — a lista de assistentes da conta.
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import assistenteIaApi from 'dashboard/api/assistenteIa';
import { useAssistenteIaApi } from '../composables/useAssistenteIaApi';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import TabelaVazia from '../components/TabelaVazia.vue';

const { t } = useI18n();
const router = useRouter();
const { accountScopedRoute } = useAccount();
const { carregando, erro, chamar } = useAssistenteIaApi();

const assistentes = ref([]);
const criando = ref(false);
const novo = ref({ nome: '', descricao: '' });
const mostrarFormulario = ref(false);
const paraApagar = ref(null);

const carregar = async () => {
  const dados = await chamar(assistenteIaApi.assistentes());
  if (dados) assistentes.value = dados.payload;
};

const criar = async () => {
  if (!novo.value.nome.trim()) return;

  criando.value = true;
  const dados = await chamar(assistenteIaApi.criarAssistente(novo.value));
  criando.value = false;
  if (!dados) return;

  mostrarFormulario.value = false;
  novo.value = { nome: '', descricao: '' };
  router.push(
    accountScopedRoute('assistente_ia_assistente', {
      assistenteId: dados.payload.id,
    })
  );
};

const apagar = async () => {
  const alvo = paraApagar.value;
  paraApagar.value = null;
  if (!alvo) return;

  const feito = await chamar(assistenteIaApi.apagarAssistente(alvo.id));
  if (feito !== null) carregar();
};

onMounted(carregar);
</script>

<template>
  <section class="flex flex-col gap-4">
    <Banner v-if="erro" color="ruby">{{ erro }}</Banner>

    <div class="flex items-center justify-between">
      <h2 class="text-heading-2 text-n-slate-12">
        {{ t('ASSISTENTE_IA.ASSISTENTES.TITULO') }}
      </h2>
      <Button
        :label="t('ASSISTENTE_IA.ASSISTENTES.NOVO')"
        icon="i-lucide-plus"
        size="sm"
        @click="mostrarFormulario = !mostrarFormulario"
      />
    </div>

    <div
      v-if="mostrarFormulario"
      class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak bg-n-solid-1"
    >
      <Input
        v-model="novo.nome"
        :label="t('ASSISTENTE_IA.ASSISTENTES.NOME')"
        :message="t('ASSISTENTE_IA.ASSISTENTES.NOME_AJUDA')"
      />
      <Input
        v-model="novo.descricao"
        :label="t('ASSISTENTE_IA.ASSISTENTES.DESCRICAO')"
      />
      <div class="flex gap-2">
        <Button
          :label="t('ASSISTENTE_IA.COMUM.CRIAR')"
          :is-loading="criando"
          size="sm"
          @click="criar"
        />
        <Button
          :label="t('ASSISTENTE_IA.COMUM.CANCELAR')"
          variant="faded"
          color="slate"
          size="sm"
          @click="mostrarFormulario = false"
        />
      </div>
    </div>

    <div v-if="carregando && !assistentes.length" class="flex justify-center py-16">
      <Spinner />
    </div>

    <TabelaVazia
      v-else-if="!assistentes.length"
      :mensagem="t('ASSISTENTE_IA.ASSISTENTES.VAZIO')"
      :acao-label="t('ASSISTENTE_IA.ASSISTENTES.NOVO')"
      @acao="mostrarFormulario = true"
    />

    <table v-else class="w-full text-sm text-left">
      <thead class="text-n-slate-11 border-b border-n-weak">
        <tr>
          <th class="py-2 font-medium">{{ t('ASSISTENTE_IA.ASSISTENTES.NOME') }}</th>
          <th class="py-2 font-medium">{{ t('ASSISTENTE_IA.ASSISTENTES.SITUACAO') }}</th>
          <th class="py-2 font-medium">{{ t('ASSISTENTE_IA.ASSISTENTES.CAIXAS') }}</th>
          <th class="py-2 font-medium">{{ t('ASSISTENTE_IA.ASSISTENTES.DOCUMENTOS') }}</th>
          <th class="py-2 font-medium text-right">
            {{ t('ASSISTENTE_IA.ASSISTENTES.ACOES') }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="assistente in assistentes"
          :key="assistente.id"
          class="border-b border-n-weak"
        >
          <td class="py-3 text-n-slate-12">
            <div class="font-medium">{{ assistente.nome }}</div>
            <div class="text-n-slate-11">{{ assistente.descricao }}</div>
          </td>
          <td class="py-3">
            <span
              class="px-2 py-0.5 text-xs rounded-full"
              :class="
                assistente.ativo
                  ? 'bg-n-teal-3 text-n-teal-11'
                  : 'bg-n-slate-3 text-n-slate-11'
              "
            >
              {{
                assistente.ativo
                  ? t('ASSISTENTE_IA.COMUM.ATIVO')
                  : t('ASSISTENTE_IA.COMUM.INATIVO')
              }}
            </span>
          </td>
          <td class="py-3 text-n-slate-11">{{ assistente.caixas_vinculadas }}</td>
          <td class="py-3 text-n-slate-11">{{ assistente.documentos_prontos }}</td>
          <td class="py-3">
            <div class="flex justify-end gap-2">
              <router-link
                :to="
                  accountScopedRoute('assistente_ia_assistente', {
                    assistenteId: assistente.id,
                  })
                "
                class="text-n-blue-11 hover:underline"
              >
                {{ t('ASSISTENTE_IA.COMUM.EDITAR') }}
              </router-link>
              <router-link
                :to="
                  accountScopedRoute('assistente_ia_base', {
                    assistenteId: assistente.id,
                  })
                "
                class="text-n-blue-11 hover:underline"
              >
                {{ t('ASSISTENTE_IA.ASSISTENTES.BASE') }}
              </router-link>
              <router-link
                :to="
                  accountScopedRoute('assistente_ia_playground', {
                    assistenteId: assistente.id,
                  })
                "
                class="text-n-blue-11 hover:underline"
              >
                {{ t('ASSISTENTE_IA.ASSISTENTES.PLAYGROUND') }}
              </router-link>
              <button
                class="text-n-ruby-11 hover:underline"
                @click="paraApagar = assistente"
              >
                {{ t('ASSISTENTE_IA.COMUM.APAGAR') }}
              </button>
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    <div
      v-if="paraApagar"
      class="flex flex-col gap-2 p-4 border rounded-xl border-n-ruby-4 bg-n-ruby-2"
    >
      <p class="mb-0 font-medium text-n-slate-12">
        {{ t('ASSISTENTE_IA.ASSISTENTES.APAGAR_TITULO') }}
      </p>
      <p class="mb-0 text-n-slate-11">
        {{ t('ASSISTENTE_IA.ASSISTENTES.APAGAR_TEXTO') }}
      </p>
      <div class="flex gap-2">
        <Button
          :label="t('ASSISTENTE_IA.COMUM.APAGAR')"
          color="ruby"
          size="sm"
          @click="apagar"
        />
        <Button
          :label="t('ASSISTENTE_IA.COMUM.CANCELAR')"
          variant="faded"
          color="slate"
          size="sm"
          @click="paraApagar = null"
        />
      </div>
    </div>
  </section>
</template>
